import { useEffect, useId, useRef, useState } from 'react';
import {
  createLocalFileObjectUrl,
  resolvePaymentProofUrl,
} from '../lib/paymentProofStore.js';
import {
  FileTypeIcon,
  RemoveFileButton,
  ViewableFileName,
} from './EvidenceUpload.jsx';
import { MESSAGE_ATTACHMENT_ACCEPT, filterMessageAttachmentFiles } from '../lib/messageAttachments.js';

function ProofFileName({ name, url }) {
  return <ViewableFileName name={name} url={url} />;
}

/**
 * Recorded payment proofs — openable when the blob was stored locally.
 * Matches demand Evidence file-list styling.
 */
export default function PaymentProofAttachments({
  matterId,
  paymentId,
  names = [],
}) {
  const [urls, setUrls] = useState({});
  const nameList = (names || []).map((n) => String(n || '').trim()).filter(Boolean);
  const nameKey = nameList.join('\u0000');

  useEffect(() => {
    let cancelled = false;
    const created = [];

    if (!matterId || !paymentId || !nameList.length) {
      setUrls({});
      return undefined;
    }

    (async () => {
      const next = {};
      for (const name of nameList) {
        const url = await resolvePaymentProofUrl(matterId, paymentId, name);
        if (url) {
          created.push(url);
          next[name] = url;
        }
      }
      if (!cancelled) setUrls(next);
    })();

    return () => {
      cancelled = true;
      for (const url of created) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
      }
    };
    // nameList is derived from nameKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId, paymentId, nameKey]);

  if (!nameList.length) return null;

  return (
    <ul className="evidence-file-list matter-payment-proof-list">
      {nameList.map((name) => (
        <li key={name}>
          <FileTypeIcon name={name} />
          <ProofFileName name={name} url={urls[name]} />
        </li>
      ))}
    </ul>
  );
}

/**
 * Pending proof row with Evidence styling + live preview before Record Payment.
 */
export function PendingPaymentProofItem({ file, onRemove, disabled = false }) {
  const [url, setUrl] = useState(null);
  const name = String(file?.name || '').trim();

  useEffect(() => {
    const objectUrl = createLocalFileObjectUrl(file);
    setUrl(objectUrl);
    return () => {
      if (objectUrl) {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch {
          // ignore
        }
      }
    };
  }, [file]);

  if (!name) return null;

  return (
    <li>
      <FileTypeIcon name={name} />
      <ProofFileName name={name} url={url} />
      <RemoveFileButton
        label={`Remove ${name}`}
        disabled={disabled}
        onClick={() => onRemove?.(name)}
      />
    </li>
  );
}

/**
 * Proof-of-payment uploader matching demand Evidence dropzone + file list.
 */
export function PaymentProofUploader({
  files = [],
  onFilesSelected,
  onRemoveFile,
  disabled = false,
  hint = 'Receipt, remittance, or bank confirmation (PDF, images, Word, or Excel)',
}) {
  const inputId = useId();
  const inputRef = useRef(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const hasFiles = (files || []).length > 0;

  function openPicker() {
    if (disabled) return;
    inputRef.current?.click();
  }

  function acceptFiles(fileList) {
    const accepted = filterMessageAttachmentFiles(fileList);
    if (!accepted.length) return;
    onFilesSelected?.(accepted);
  }

  function onInputChange(e) {
    acceptFiles(e.target.files);
    e.target.value = '';
  }

  function onDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    dragDepth.current += 1;
    setDragging(true);
  }

  function onDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  }

  function onDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setDragging(false);
    if (disabled) return;
    acceptFiles(e.dataTransfer?.files);
  }

  return (
    <div
      className={`matter-payment-proof-upload evidence-upload${hasFiles ? ' has-files' : ''}${
        disabled ? ' is-disabled' : ''
      }`}
    >
      {hasFiles ? (
        <ul className="evidence-file-list">
          {files.map((file) => (
            <PendingPaymentProofItem
              key={file.name}
              file={file}
              disabled={disabled}
              onRemove={onRemoveFile}
            />
          ))}
        </ul>
      ) : null}

      <div
        className={[
          'evidence-dropzone',
          hasFiles ? 'is-compact' : '',
          dragging && !disabled ? 'is-dragging' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <input
          ref={inputRef}
          id={inputId}
          className="evidence-dropzone-input"
          type="file"
          accept={MESSAGE_ATTACHMENT_ACCEPT}
          multiple
          tabIndex={-1}
          disabled={disabled}
          onChange={onInputChange}
          onClick={(e) => e.stopPropagation()}
        />
        <div className="evidence-dropzone-copy">
          {hasFiles ? (
            <span>
              Drop more files here or{' '}
              <button
                type="button"
                className="text-link evidence-dropzone-browse"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  openPicker();
                }}
              >
                browse
              </button>
            </span>
          ) : (
            <>
              <strong>Drag & drop files here</strong>
              <span>
                or{' '}
                <button
                  type="button"
                  className="text-link evidence-dropzone-browse"
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    openPicker();
                  }}
                >
                  browse
                </button>{' '}
                · {hint}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
